import { useState } from 'react';
import {
  useFriends, useInviteFriend, useAcceptFriend, useRemoveFriend,
  type FriendRow,
} from '../lib/api';

export function FriendsPage() {
  const friends = useFriends();
  const accept = useAcceptFriend();
  const remove = useRemoveFriend();
  const invite = useInviteFriend();
  const [q, setQ] = useState('');

  const list = friends.data?.friends ?? [];
  const accepted = list.filter((f) => f.status === 'accepted');
  const incoming = list.filter((f) => f.status === 'pending' && f.direction === 'incoming');
  const outgoing = list.filter((f) => f.status === 'pending' && f.direction === 'outgoing');

  const handleInvite = async () => {
    const val = q.trim();
    if (!val) return;
    invite.reset();
    if (val.includes('@')) {
      await invite.mutateAsync({ email: val });
    } else {
      await invite.mutateAsync({ handle: val });
    }
    setQ('');
  };

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-2xl font-semibold text-squishy-dark">Friends</h1>

      {/* ── Add friend ───────────────────────────────────────── */}
      <section className="bg-white border rounded-xl p-5 space-y-3">
        <h2 className="font-medium">Add a friend</h2>
        <p className="text-xs text-slate-500">Enter their name, email, or username. If they have an account, they'll receive a friend invite.</p>
        <form
          onSubmit={(e) => { e.preventDefault(); handleInvite(); }}
          className="flex gap-2"
        >
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); invite.reset(); }}
            placeholder="Name, email, or username"
            className="flex-1 border rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={invite.isPending || q.trim().length < 2}
            className="bg-squishy-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          >Send</button>
        </form>
        {invite.isSuccess && (
          <p className="text-xs text-emerald-600">
            If that user exists, they'll receive your friend invite!
          </p>
        )}
        {invite.isError && (
          <p className="text-xs text-red-500">Something went wrong. Please try again.</p>
        )}
      </section>

      {/* ── Incoming requests ────────────────────────────────── */}
      {incoming.length > 0 && (
        <section className="bg-white border rounded-xl p-5">
          <h2 className="font-medium mb-3">Friend requests ({incoming.length})</h2>
          <ul className="divide-y">
            {incoming.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2">
                <Avatar name={formatRow(f)} hasAvatar={Boolean(f.avatar_s3_key)} />
                <div className="flex-1 text-sm font-medium">{formatRow(f)}</div>
                <button
                  onClick={() => accept.mutate(f.id)}
                  className="bg-squishy-primary text-white px-3 py-1 text-xs rounded-full"
                >Accept</button>
                <button
                  onClick={() => remove.mutate(f.id)}
                  className="border px-3 py-1 text-xs rounded-full text-slate-500"
                >Decline</button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Outgoing requests ────────────────────────────────── */}
      {outgoing.length > 0 && (
        <section className="bg-white border rounded-xl p-5">
          <h2 className="font-medium mb-3">Sent ({outgoing.length})</h2>
          <ul className="divide-y">
            {outgoing.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2 text-sm">
                <Avatar name={formatRow(f)} hasAvatar={Boolean(f.avatar_s3_key)} />
                <div className="flex-1 font-medium">{formatRow(f)}</div>
                <span className="text-xs text-slate-400">pending</span>
                <button onClick={() => remove.mutate(f.id)}
                        className="text-xs text-slate-500 hover:text-red-600">cancel</button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Friends list ─────────────────────────────────────── */}
      <section className="bg-white border rounded-xl p-5">
        <h2 className="font-medium mb-3">Your friends ({accepted.length})</h2>
        {accepted.length === 0 ? (
          <p className="text-sm text-slate-500">No friends yet — add someone above.</p>
        ) : (
          <ul className="divide-y">
            {accepted.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2 text-sm">
                <Avatar name={formatRow(f)} hasAvatar={Boolean(f.avatar_s3_key)} />
                <div className="flex-1 font-medium">{formatRow(f)}</div>
                <button onClick={() => remove.mutate(f.id)}
                        className="text-xs text-slate-400 hover:text-red-600">remove</button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function formatRow(f: FriendRow): string {
  const real = `${f.first_name} ${f.last_name?.[0] ?? ''}.`;
  // Accepted friends always see your real name, even if you're anonymous / use a handle.
  if (f.status === 'accepted') return real;
  if (f.display_name_mode === 'custom' && f.custom_handle) return `@${f.custom_handle}`;
  if (f.display_name_mode === 'anonymous') return `Student #${f.id.slice(0, 4).toUpperCase()}`;
  return real;
}

function Avatar({ name, hasAvatar }: { name: string; hasAvatar: boolean }) {
  return (
    <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-700 overflow-hidden">
      {hasAvatar ? <img className="w-full h-full object-cover" alt="" /> : name[0]?.replace('@', '')}
    </div>
  );
}
